import {useStyletron} from "baseui";
import {Cell, Grid} from "baseui/layout-grid";
import {ParagraphMedium, ParagraphXSmall} from "baseui/typography";
import React, {useEffect, useState} from "react";
import {NavBar} from "./nav-bar";
import {translate, useAuth} from "@soap/modules";
import {useQueryString} from "../hooks/useQueryString";
import {useLocation} from "react-router";
import {useMediaQuery} from "react-responsive";
import {Row} from "./row";
import Alex from "/z30-images/robot-s.png"
import {LinkButton} from "./buttons";
import {BaseWebBreakPoints, HScroll, MobileOnly, SpaceTop} from "./layout";
import {PrimaryActionMenuButton} from "@soap/baseweb";
import {Facebook, Instagram, Linkedin} from "lucide-react";
import {YouTubeApiServicesNotice} from "../z70-shadcn/components/YouTubeApiServicesNotice";

/** Muted text on green footer (#58ac59); matches static marketing pages */
const FOOTER_ON_GREEN = "#cbe3e2";

const FACEBOOK_URL = "https://www.facebook.com/learnyourwayai";
const INSTAGRAM_URL = "https://www.instagram.com/learnyourwayai";
const LINKEDIN_URL = "https://www.linkedin.com/company/learn-your-way-ai";

export function Frame({children}) {

    const queryString = useQueryString();
    const [css, theme] = useStyletron();
    return (<>
        <div style={{
            minHeight: "100vh",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",

        }}>
            <Header/>
            <div style={{ flex: 1, width: "100%" }}>
                <HScroll>
                    {children}
                </HScroll>
            </div>
            <div style={{
                marginTop: "auto",

            }}>
                <Footer/>
            </div>
        </div>
    </>);
}

function Footer() {
    const [css, theme] = useStyletron();
    const [version, setVersion] = useState('');
    const {isAuthenticated} = useAuth();
    const location = useLocation();
    const isContactPage = location.pathname === '/help' || location.pathname === '/contact-us';
    const isFooterDesktop = useMediaQuery({query: `(min-width: ${BaseWebBreakPoints.Third}px)`});
    const showNeedHelpAside =
        !isAuthenticated && !isContactPage && isFooterDesktop;

    useEffect(() => {
        let cancelled = false;
        const root = import.meta.env.VITE_FUNCTIONAPP_ROOT;
        fetch(`${root}/GetVersion`)
            .then((response) => response.json())
            .then((data) => {
                if (!cancelled && data?.message != null) {
                    setVersion(data.message);
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div className={css({
            marginTop: isAuthenticated ? '50px' : '24px',
            paddingTop: '50px',
            paddingBottom: '50px',
            background: '#58ac59'
        })}>
            <Grid>
                <Cell span={showNeedHelpAside ? 8 : 12}>
                    <div className={css({
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                    })}>
                        {/* Inline flex so links are centered as a group (ParagraphMedium is full-width and was left-aligning the buttons) */}
                        <div className={css({
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            alignItems: 'center',
                            columnGap: theme.sizing.scale900,
                            rowGap: theme.sizing.scale300,
                        })}>
                            <LinkButton
                                colour={FOOTER_ON_GREEN}
                                hoverColour={FOOTER_ON_GREEN}
                                hover={true}
                                underline={true}
                                onClick={() => {
                                    window.location.href = '/privacy';
                                }}>
                                {translate("Privacy Policy")}</LinkButton>
                            <LinkButton
                                hover={true}
                                underline={true}
                                colour={FOOTER_ON_GREEN}
                                hoverColour={FOOTER_ON_GREEN}
                                onClick={() => {
                                    window.location.href = '/terms';
                                }}>
                                {translate("Terms and Conditions")}</LinkButton>
                        </div>
                        <SpaceTop>
                            <YouTubeApiServicesNotice variant="footer" />
                        </SpaceTop>
                        <SpaceTop>
                            <div className={css({
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: theme.sizing.scale600,
                            })}>
                                <a
                                    href={FACEBOOK_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Learn Your Way on Facebook"
                                    className={css({
                                        color: FOOTER_ON_GREEN,
                                        display: 'inline-flex',
                                        ':hover': {opacity: 0.85},
                                    })}>
                                    <Facebook size={24} aria-hidden="true"/>
                                </a>
                                <a
                                    href={INSTAGRAM_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Learn Your Way on Instagram"
                                    className={css({
                                        color: FOOTER_ON_GREEN,
                                        display: 'inline-flex',
                                        ':hover': {opacity: 0.85},
                                    })}>
                                    <Instagram size={24} aria-hidden="true"/>
                                </a>
                                <a
                                    href={LINKEDIN_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Learn Your Way on LinkedIn"
                                    className={css({
                                        color: FOOTER_ON_GREEN,
                                        display: 'inline-flex',
                                        ':hover': {opacity: 0.85},
                                    })}>
                                    <Linkedin size={24} aria-hidden="true"/>
                                </a>
                            </div>
                        </SpaceTop>
                        <SpaceTop>
                            <div className={css({textAlign: 'center'})}>
                                <ParagraphXSmall
                                    color={FOOTER_ON_GREEN}>&copy; {new Date().getFullYear()} Learn Your Way
                                    Ltd, Ver. {version}</ParagraphXSmall>
                            </div>
                        </SpaceTop>
                    </div>
                </Cell>
                {showNeedHelpAside && (
                    <Cell span={4}>
                        <NeedHelp/>
                    </Cell>
                )}
            </Grid>
        </div>
    );
}

export function NeedHelp() {
    const [css, theme] = useStyletron();
    return (
        <Row spacing={"0px"}>
            <div className={css({})}>
                <PrimaryActionMenuButton onClick={() => {
                    location.href = "/help";
                    window.scrollTo(0, 0);
                }}><ParagraphMedium
                    color={FOOTER_ON_GREEN}> {translate("Need Help?")}</ParagraphMedium></PrimaryActionMenuButton>
            </div>
            <div className={css({
                marginLeft: "20px",
                marginTop: "15px"
            })}>
                <img src={Alex} width={75} alt={translate("Need Help?")}/>
            </div>
        </Row>
    );
}

function Header() {
    const [css, theme] = useStyletron();

    return (
        <div style={{ width: "100%", paddingBottom: 8, borderBottom: "1px solid #f0f0f0" }}>
            <div className="lyw-content-outer" style={{ paddingTop: 12 }}>
                <div className="lyw-content-inner">
                    <NavBar/>
                </div>
            </div>
        </div>
    )
}